import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { IMG } from "@/lib/images";
import { ArrowRight, Radio, Clock, Users, CheckCircle2 } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { mediaUrl } from "@/lib/api";

const EQUIPMENT = ["Neumann U87", "Sennheiser MKH416", "Trident S80 pre-amp", "ADL1000 tube compressor",
  "SSL G-channel", "DBX160", "Quested / KRK / Avantone monitors", "Pro Tools 2020.5",
  "Focusrite Scarlett 18i20", "StudioBricks vocal booth"];
const REMOTE = ["Source Connect", "Session Link Pro", "Cleanfeed", "ISDN", "Skype", "Zoom"];

export default function Studio() {
  const { c } = useContent();
  const eqLines = (c("studio_equipment") || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const equipment = eqLines.length ? eqLines : EQUIPMENT;
  return (
    <>
      <SEO title="Recording Studio & Remote Direction" path="/studio"
        description="VoiceOverKids' in-house recording studio in the Netherlands plus partner studios worldwide. Remote direction via Source Connect, Session Link Pro, Cleanfeed and Zoom." />
      <section className="relative overflow-hidden bg-brand-ink text-white">
        <div className="absolute inset-0"><img src={IMG.micPop} alt="Studio microphone with pop filter" className="h-full w-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-r from-brand-ink to-brand-ink/50" /></div>
        <div className="container-x relative py-20">
          <span className="label-eyebrow text-brand-purple">Studio & recording</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">Our recording studio</h1>
          <p className="mt-4 max-w-2xl text-zinc-300">{c("studio_intro", "A dedicated in-house recording studio in the Netherlands, plus partner studios worldwide. Every child is recorded with a specialist director — parents or caregivers always present.")}</p>
        </div>
      </section>

      {c("studio_image") && (
        <section className="container-x pt-4">
          <img src={mediaUrl(c("studio_image"))} alt="VoiceOverKids recording studio" className="w-full rounded-3xl object-cover" />
        </section>
      )}

      <section className="container-x section-y grid gap-10 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-2xl font-bold">Equipment</h2>
          <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {equipment.map((e) => <li key={e} className="flex items-center gap-2 text-zinc-700"><CheckCircle2 size={16} className="text-brand-violet" /> {e}</li>)}
          </ul>
        </div>
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Radio size={22} className="text-brand-violet" /> Remote direction</h2>
          <p className="mt-3 text-zinc-600">Connect live to our studio to listen in and direct the session yourself:</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {REMOTE.map((r) => <span key={r} className="rounded-full bg-violet-100 px-4 py-1.5 text-sm font-semibold text-brand-violet">{r}</span>)}
          </div>
          <p className="mt-4 text-sm text-zinc-500">Remote direction is always agreed with our local director and the child's parents, as some children perform best with their trusted local director.</p>
        </div>
      </section>

      <section id="turnaround" className="bg-white section-y">
        <div className="container-x grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Clock size={22} className="text-brand-violet" /> Turnaround</h2>
            <p className="mt-3 text-zinc-600">These are real children. Recordings are scheduled after school or at weekends (local timezone), with parents or guardians accompanying them. Please allow around <strong>5 days</strong> to arrange and schedule a recording. Same-day turnaround isn't realistic for human voices — for urgent needs, our <Link to="/ai-voices" className="text-brand-violet font-semibold">AI voices</Link> generate instantly.</p>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Users size={22} className="text-brand-violet" /> Rates</h2>
            <p className="mt-3 text-zinc-600">Child voice-over fees combine a basic session rate plus a buyout/usage fee, calculated by media type and territory. National broadcast, online pre-rolls and social usage are priced differently to website-only use. Our account manager will advise based on your usage and target country.</p>
          </div>
        </div>
      </section>

      <section className="container-x py-16 text-center">
        <Link to="/casting" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-8 py-4 font-bold text-white transition hover:bg-brand-violetHover">Start a casting <ArrowRight size={18} /></Link>
      </section>
    </>
  );
}
